import { displayText } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import api from '../../services/api';
import ThemeContext from '../../ThemeContext';
import Pagination from '../../Utils/Pagination';
import AppConfig from '../../../config/applicationconfig';

const EnterpriseClients = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const { username } = useParams();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  /* ---------------- THEME TYPOGRAPHY ---------------- */
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  /* ---------------- STATE ---------------- */
  const [enterpriseClients, setEnterpriseClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentUser] = useState(
    JSON.parse(localStorage.getItem('user') || '{}')
  );

  const [isSuperAdmin] = useState(currentUser.role === 'superadmin');
  const [isAdmin] = useState(currentUser.role === 'admin');
  const [isReseller] = useState(currentUser.role === 'reseller');

  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedClientId, setHighlightedClientId] = useState(null);
  const [highlightedField, setHighlightedField] = useState(null);
  const [hoveredClientId, setHoveredClientId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [gotoPage, setGotoPage] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);

  const [sortField, setSortField] = useState('client_name');
  const [sortOrder, setSortOrder] = useState('asc');

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    const fetchEnterpriseClients = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/enterprise-clients');
        setEnterpriseClients(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        toast.error(displayText('Failed to fetch enterprise clients.'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchEnterpriseClients();
  }, []);

  /* ---------------- SEARCH ---------------- */
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedClientId(null);
    setHighlightedField(null);
    setCurrentPage(1);
  };

  const handleSearchSubmit = () => {
    const term = searchTerm.toLowerCase();
    const matched = enterpriseClients.find(
      (c) =>
        `${c.id}` === term ||
        c.client_name?.toLowerCase().includes(term) ||
        c.primary_name?.toLowerCase().includes(term) ||
        c.reseller?.username?.toLowerCase().includes(term) ||
        c.status?.toLowerCase().includes(term)
    );

    if (!matched) {
      setHighlightedClientId(null);
      setHighlightedField(null);
      return;
    }

    setHighlightedClientId(matched.id);

    if (`${matched.id}` === term) setHighlightedField('id');
    else if (matched.client_name?.toLowerCase().includes(term))
      setHighlightedField('client_name');
    else if (matched.primary_name?.toLowerCase().includes(term))
      setHighlightedField('primary_name');
    else if (matched.reseller?.username?.toLowerCase().includes(term))
      setHighlightedField('sponsor');
    else setHighlightedField('status');
  };

  /* ---------------- SORT ---------------- */
  const handleSort = (field) => {
    if (sortField === field)
      setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortValue = (c, field) => {
    if (field === 'start_date') return c.start_date ? new Date(c.start_date) : null;
    return (c[field] || '').toString().toLowerCase();
  };

  /* ---------------- FILTER + SORT ---------------- */
  const filteredClients = enterpriseClients
    .filter(
      (c) =>
        c.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.primary_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.reseller?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${c.id}`.includes(searchTerm)
    )
    .sort((a, b) => {
      const A = getSortValue(a, sortField);
      const B = getSortValue(b, sortField);
      return sortOrder === 'asc' ? (A > B ? 1 : -1) : A < B ? 1 : -1;
    });

  const paginated = filteredClients.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  /* ---------------- ACTION ---------------- */
  const handleActivateDeactivate = (id) => {
    setSelectedClientId(id);
    setShowModal(true);
  };

  const confirmAction = async () => {
    const client = enterpriseClients.find((c) => c.id === selectedClientId);
    if (!client) return;

    const newStatus = client.status === 'Active' ? 'Deactivated' : 'Active';

    setEnterpriseClients((prev) =>
      prev.map((c) =>
        c.id === selectedClientId ? { ...c, status: newStatus } : c
      )
    );

    try {
      await api.put(`/enterprise-clients/${selectedClientId}/status`, {
        status: newStatus,
      });
      toast.success(`${displayText("Client ")}${newStatus.toLowerCase()}${displayText(" successfully.")}`);
    } catch {
      toast.error('Failed to update status.');
      // rollback on error
      setEnterpriseClients((prev) =>
        prev.map((c) =>
          c.id === selectedClientId ? { ...c, status: client.status } : c
        )
      );
    } finally {
      setShowModal(false);
      setSelectedClientId(null);
    }
  };

  const basePath = isSuperAdmin
    ? '/superadmin'
    : isAdmin
    ? '/admin'
    : `/${username}`;

  /* ---------------- RENDER ---------------- */
  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      {/* WHITE CARD CONTAINER - Same as Resellers & Clients */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "1rem",
          borderRadius: "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          fontFamily,
          marginTop: "10px",
        }}
      >
        <ToastContainer />

        {/* Loading Spinner */}
        {isLoading && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1050,
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: `4px solid ${theme.colors.primary}`,
                borderTop: '4px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        )}

        {/* HEADER */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            gap: '0.5rem',
          }}
        >
          <h4
            style={{
              fontSize: `${titleFontSize}px`,
              fontWeight: 'bold',
              marginBottom: 0,
              color: theme.colors.textPrimary,
              fontFamily,
            }}
          >{displayText(" Enterprise Clients ")}</h4>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end',
            }}
          >
            {/* Search Input */}
            <div style={{ position: "relative", maxWidth: "140px", height: "38px" }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={displayText("Search clients...")}
                value={searchTerm}
                onChange={handleSearch}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "1px solid #dee2e6",
                  borderRadius: "25px",
                  outline: "none",
                  padding: "0 40px 0 14px",
                  fontSize: "12px",
                  backgroundColor: "#ffffff",
                  fontFamily,
                }}
              />
              <button
                onClick={handleSearchSubmit}
                style={{
                  position: "absolute",
                  right: "2px",
                  top: "2px",
                  width: "34px",
                  height: "34px",
                  background: theme.gradients?.primary || theme.colors.primary,
                  border: "none",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-search" style={{ color: "#fff", fontSize: "14px" }}></i>
              </button>
            </div>

            {isReseller && (
              <Link
                to={`/${username}/enterprise-client/add`}
                style={{
                  background: theme.gradients?.secondary || theme.colors.secondary,
                  color: theme.colors.textWhite,
                  padding: "6px 12px",
                  borderRadius: "8px",
                  fontSize: "clamp(10px, 2.5vw, 11px)",
                  textDecoration: "none",
                  minWidth: "140px",
                  textAlign: "center",
                  fontFamily,
                }}
              >{displayText(" Add Enterprise Client ")}</Link>
            )}
          </div>
        </div>

        {/* TOP PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={filteredClients.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* TABLE CONTAINER */}
        <div
          style={{
            overflowX: "auto",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #dee2e6",
            marginTop: "1rem",
            marginBottom: "1rem",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: `${bodyFontSize}px`,
              fontFamily,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    ID
                    {sortField === 'id' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('client_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>{displayText(" Client Name ")}{sortField === 'client_name' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('primary_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {AppConfig.firstname} Contact
                    {sortField === 'primary_name' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('sponsor')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Sponsor
                    {sortField === 'sponsor' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('start_date')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>{displayText(" Client Since ")}{sortField === 'start_date' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Status
                    {sortField === 'status' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length > 0 ? (
                paginated.map((c) => (
                  <tr
                    key={c.id}
                    onMouseEnter={() => setHoveredClientId(c.id)}
                    onMouseLeave={() => setHoveredClientId(null)}
                    style={{ borderBottom: "1px solid #dee2e6" }}
                  >
                    <td className="p-2" style={{ backgroundColor: c.id === highlightedClientId && highlightedField === 'id' ? '#FFD700' : '#fff', fontFamily }}>
                      {c.id}
                    </td>
                    <td className="p-2" style={{ backgroundColor: c.id === highlightedClientId && highlightedField === 'client_name' ? '#FFD700' : '#fff', fontFamily }}>
                      {c.client_name || 'N/A'}
                    </td>
                    <td className="p-2" style={{ backgroundColor: c.id === highlightedClientId && highlightedField === 'primary_name' ? '#FFD700' : '#fff', fontFamily }}>
                      {c.primary_name || 'N/A'}
                    </td>
                    <td className="p-2" style={{ backgroundColor: c.id === highlightedClientId && highlightedField === 'sponsor' ? '#FFD700' : '#fff', fontFamily }}>
                      {c.reseller?.username || 'None'}
                    </td>
                    <td className="p-2" style={{ fontFamily }}>
                      {c.start_date ? new Date(c.start_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-2" style={{ fontFamily }}>
                      <span style={{ color: c.status === 'Active' ? theme.colors.success : theme.colors.danger, fontWeight: 'bold' }}>
                        {c.status || 'N/A'}
                      </span>
                    </td>
                    <td className="p-2 position-relative">
                      {hoveredClientId === c.id && (
                        <div
                          style={{
                            position: "absolute",
                            right: "0.5rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            display: "flex",
                            gap: "0.5rem",
                          }}
                        >
                          <Link
                            to={`${basePath}/enterprise-client/edit/${c.id}`}
                            style={{
                              color: theme.colors.primary,
                              textDecoration: "underline",
                              fontSize: `${bodyFontSize}px`,
                              fontFamily,
                            }}
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleActivateDeactivate(c.id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: c.status === 'Active' ? theme.colors.danger : theme.colors.success,
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: `${bodyFontSize}px`,
                              padding: 0,
                              fontFamily,
                            }}
                          >
                            {c.status === 'Active' ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-center p-3" colSpan={7} style={{ fontFamily }}>
                    {searchTerm ? `${displayText("No enterprise client found matching \"")}${searchTerm}${displayText("\"")}` : displayText('No enterprise clients found.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* BOTTOM PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={filteredClients.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* CONFIRMATION MODAL */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "320px",
                textAlign: "center",
                fontFamily,
              }}
            >
              <h5 style={{ marginBottom: "10px", color: theme.colors.danger, fontSize: `${titleFontSize}px`, fontFamily }}>
                Confirm Action
              </h5>
              <p style={{ fontSize: `${bodyFontSize}px`, fontFamily }}>{displayText(" Are you sure you want to toggle this client's status? ")}</p>
              <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.textWhite,
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "5px",
                    fontFamily,
                    fontSize: `${bodyFontSize}px`,
                  }}
                >
                  No
                </button>
                <button
                  onClick={confirmAction}
                  style={{
                    backgroundColor: theme.colors.danger,
                    color: theme.colors.textWhite,
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "5px",
                    fontFamily,
                    fontSize: `${bodyFontSize}px`,
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnterpriseClients;